import React from "react";
import { Package, Star, ShoppingCart, Shield, Heart, Zap, Globe, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const Features = () => {
  const features = [
    {
      icon: <Package className="h-8 w-8 text-white" />,
      title: "Authentic Products",
      description: "Every item in our care packages is sourced directly from Jamaica, ensuring authentic tastes and experiences.",
      gradient: "from-jamaica-green to-emerald-600",
      accent: "jamaica-green",
      delay: 0.1
    },
    {
      icon: <Globe className="h-8 w-8 text-white" />,
      title: "Global Shipping",
      description: "We ship our care packages worldwide, bringing a piece of Jamaica to homesick nationals and curious explorers alike.",
      gradient: "from-jamaica-yellow to-amber-500",
      accent: "jamaica-yellow", 
      delay: 0.2
    },
    {
      icon: <Heart className="h-8 w-8 text-white" />,
      title: "Custom Packages",
      description: "Create your own personalized care package with your favourite Jamaican treats and treasures.",
      gradient: "from-red-500 to-pink-600",
      accent: "red-500",
      delay: 0.3
    },
  ];

  return (
    <div className="relative bg-gradient-to-b from-white via-gray-50/50 to-white py-20 sm:py-24 overflow-hidden">
      {/* Unique Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Diagonal Floating Lines */}
        <div className="absolute top-10 left-10 w-40 h-1 bg-gradient-to-r from-jamaica-green/20 to-transparent transform rotate-12 animate-pulse delay-200"></div>
        <div className="absolute top-32 right-20 w-32 h-1 bg-gradient-to-l from-jamaica-yellow/20 to-transparent transform -rotate-12 animate-pulse delay-400"></div>
        <div className="absolute bottom-20 left-1/3 w-36 h-1 bg-gradient-to-r from-jamaica-green/15 to-transparent transform rotate-45 animate-pulse delay-600"></div>
        
        {/* Diamond Shapes */}
        <div className="absolute top-1/4 right-1/4 w-8 h-8 bg-jamaica-yellow/10 transform rotate-45 animate-spin delay-1000" style={{ animationDuration: '20s' }}></div>
        <div className="absolute bottom-1/3 left-1/5 w-6 h-6 bg-jamaica-green/10 transform rotate-45 animate-spin delay-1500" style={{ animationDuration: '25s' }}></div>
        
        {/* Floating Particles */}
        <div className="absolute top-20 right-40 w-2 h-2 bg-jamaica-yellow rounded-full animate-bounce delay-700"></div>
        <div className="absolute bottom-40 right-20 w-3 h-3 bg-jamaica-green rounded-full animate-bounce delay-900"></div>
        <div className="absolute top-2/3 left-20 w-2 h-2 bg-jamaica-yellow rounded-full animate-ping delay-1100"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
            <div className="relative">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-xl flex items-center justify-center shadow-lg transform -rotate-12 landing-features-icon">
                <Shield className="w-4.5 h-4.5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-jamaica-yellow rounded-full flex items-center justify-center animate-pulse">
                <Sparkles className="w-1.5 h-1.5 sm:w-2 sm:h-2 text-white" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-4 sm:py-2 rounded-2xl border border-jamaica-green/30 shadow-lg">
              <span className="text-xs sm:text-sm font-bold text-jamaica-black flex items-center gap-2">
                <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-jamaica-green" />
                Quality Promise
              </span>
            </div>
          </div>
          
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4">
            <span className="bg-gradient-to-r from-gray-900 via-jamaica-green to-gray-900 bg-clip-text text-transparent p-1">
              Why Choose Us
            </span>
          </h2>
          <p className="hidden sm:block text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We take pride in bringing the true essence of Jamaica to your doorstep with 
            <span className="text-jamaica-green font-semibold"> exceptional quality</span> and 
            <span className="text-jamaica-yellow font-semibold"> authentic experiences</span>
          </p>
          <p className="sm:hidden text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
            Bringing the essence of Jamaica with exceptional quality and authentic experiences.
          </p>
        </motion.div>
        
        {/* Enhanced Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 0.7, 
                delay: feature.delay,
                ease: "easeOut"
              }}
              viewport={{ once: true }}
              className="group relative"
            >
              {/* Background Card with Unique Shape */}
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/60 overflow-hidden landing-features-card">
                
                {/* Animated Border Accent */}
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                {/* Floating Icon Container */}
                <div className="relative mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center shadow-xl relative z-10 landing-features-card-icon`}>
                    {feature.icon}
                  </div>
                  {/* Icon Glow Effect */}
                  <div className={`absolute inset-0 w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-500 blur-lg`}></div>
                  
                  {/* Floating Sparkles */}
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-jamaica-yellow/20 rounded-full animate-ping group-hover:animate-pulse"></div>
                  <div className="absolute -bottom-1 -left-1 w-4 h-4 bg-jamaica-green/20 rounded-full animate-pulse delay-300"></div>
                </div>
                
                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 group-hover:text-jamaica-green transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors duration-300">
                    {feature.description}
                  </p>
                </div>

                {/* Decorative Corner Elements */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gray-200/50 group-hover:border-jamaica-green/50 transition-colors duration-500"></div>
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gray-200/50 group-hover:border-jamaica-yellow/50 transition-colors duration-500"></div>

                {/* Hover Gradient Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-3xl`}></div>
              </div>

              {/* External Floating Elements */}
              <div className={`absolute -top-4 -right-4 w-8 h-8 bg-${feature.accent}/10 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200 animate-bounce`}></div>
              <div className={`absolute -bottom-4 -left-4 w-6 h-6 bg-${feature.accent}/10 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-300 animate-pulse`}></div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Call-to-Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-jamaica-green/5 to-jamaica-yellow/5 px-6 py-3 rounded-2xl border border-jamaica-green/20 shadow-lg landing-features-cta">
            <Heart className="w-5 h-5 text-jamaica-green animate-pulse" />
            <span className="text-jamaica-black font-semibold">Trusted by many worldwide</span>
            <Sparkles className="w-4 h-4 text-jamaica-yellow animate-pulse delay-500" />
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Features;
